/**
 * DiscRadar — Lightweight SVG radar/spider for a single DISC profile.
 * Pure SVG, no chart library. Tweak via the colours map.
 */

import { DISC_COLOURS } from "../EmploymentIntelligence";

export default function DiscRadar({ scores, size = 220, comparison = null }) {
  // Geometry — 4 axes at 0°, 90°, 180°, 270°
  const cx = size / 2;
  const cy = size / 2;
  const max = size * 0.4;
  const axes = [
    { letter: "D", angle: -Math.PI / 2, score: scores?.dominance || 0 },
    { letter: "I", angle: 0,            score: scores?.influence || 0 },
    { letter: "S", angle: Math.PI / 2,  score: scores?.steadiness || 0 },
    { letter: "C", angle: Math.PI,      score: scores?.compliance || 0 },
  ];
  const point = (angle, value) => {
    const r = (value / 100) * max;
    return [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];
  };
  const polygonPath = (vals) =>
    vals.map((v, i) => point(axes[i].angle, v)).map((p, i) => `${i === 0 ? "M" : "L"}${p[0]},${p[1]}`).join(" ") + " Z";

  const grid = [25, 50, 75, 100];
  const labelOffset = max + 18;

  return (
    <svg viewBox={`0 0 ${size} ${size}`} width="100%" role="img" aria-label="DISC radar" style={{ height: "auto", display: "block" }}>
      {/* Rings */}
      {grid.map((g) => (
        <polygon
          key={g}
          points={axes
            .map((a) => {
              const r = (g / 100) * max;
              return `${cx + r * Math.cos(a.angle)},${cy + r * Math.sin(a.angle)}`;
            })
            .join(" ")}
          fill="none"
          stroke="#e6e7e2"
          strokeWidth="1"
        />
      ))}
      {/* Axes */}
      {axes.map((a) => {
        const [x, y] = point(a.angle, 100);
        return <line key={a.letter} x1={cx} y1={cy} x2={x} y2={y} stroke="#e6e7e2" strokeWidth="1" />;
      })}
      {/* Comparison polygon (e.g. company average) */}
      {comparison && (
        <polygon
          points={axes
            .map((a, i) => {
              const v = [comparison.dominance, comparison.influence, comparison.steadiness, comparison.compliance][i] || 0;
              const [x, y] = point(a.angle, v);
              return `${x},${y}`;
            })
            .join(" ")}
          fill="rgba(15, 23, 42, 0.07)"
          stroke="#94a3b8"
          strokeWidth="1.5"
          strokeDasharray="3 3"
        />
      )}
      {/* Main polygon */}
      <path
        d={polygonPath([
          scores?.dominance || 0,
          scores?.influence || 0,
          scores?.steadiness || 0,
          scores?.compliance || 0,
        ])}
        fill="rgba(14, 165, 233, 0.18)"
        stroke="#0ea5e9"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      {/* Dots at each apex */}
      {axes.map((a) => {
        const [x, y] = point(a.angle, a.score);
        return (
          <circle
            key={`d-${a.letter}`}
            cx={x}
            cy={y}
            r="4"
            fill={DISC_COLOURS[a.letter].fill}
            stroke="#fff"
            strokeWidth="1.5"
          />
        );
      })}
      {/* Labels */}
      {axes.map((a) => {
        const [lx, ly] = [cx + labelOffset * Math.cos(a.angle), cy + labelOffset * Math.sin(a.angle)];
        return (
          <g key={`l-${a.letter}`}>
            <circle cx={lx} cy={ly} r="14" fill={DISC_COLOURS[a.letter].fill} />
            <text
              x={lx}
              y={ly}
              fontSize="13"
              fontWeight="800"
              fill="#fff"
              textAnchor="middle"
              dominantBaseline="middle"
            >
              {a.letter}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
